import { Singleton } from 'typescript-ioc/es5'
import aqe from '@pushly/aqe'
import { HttpMethod } from '@pushly/aqe/lib/enums'
import { IApiCallOptions, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { AbstractRequestService } from '@pushly/aqe/lib/services/abstract-request-service'
import { AiGenerationDirective } from '@pushly/models/lib/structs/pushly-ai/ai-generation-directive'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { CreateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/create-ai-generation-directive-request'
import { UpdateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/update-ai-generation-directive-request'
import { CompileAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/compile-ai-generation-directive-request'
import { CompileAndPersistAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/compile-and-persist-ai-generation-directive-request'
import { SuggestDirectivePromotionsRequest } from '@pushly/models/lib/structs/pushly-ai/suggest-directive-promotions-request'
import { SuggestDirectivePromotionsResponse } from '@pushly/models/lib/structs/pushly-ai/suggest-directive-promotions-response'

/**
 * Client for the sailfish AI generation directive CRUD:
 *   GET   /domains/:domainId/ai-generation-directives[?role=]
 *   POST  /domains/:domainId/ai-generation-directives
 *   PATCH /domains/:domainId/ai-generation-directives/:directiveId
 *
 * The sailfish controller runs with its payload case-converter disabled and
 * validates the snake-case `@pushly/models` structs, so this service uses those
 * structs directly with `skipCaseConversion` (no camelCase bridging).
 */
@Singleton
export class AiGenerationDirectiveService extends AbstractRequestService {
    public constructor() {
        super()
    }

    /** Resolve the domain's Brand Voice directive (the singleton `brand_voice`), or null. */
    public async fetchBrandVoice(
        domainId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<AiGenerationDirective | null>> {
        const res = await this.request(HttpMethod.GET, `${this.getEndpoint(domainId)}?role=brand_voice`, {
            ...options,
            config: { skipCaseConversion: true },
            responseType: [AiGenerationDirective],
        })

        return { ...res, data: res.data?.[0] ?? null }
    }

    public async create(
        domainId: number,
        dto: CreateAiGenerationDirectiveRequest,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<AiGenerationDirective>> {
        return this.request(HttpMethod.POST, this.getEndpoint(domainId), {
            ...options,
            data: dto,
            config: { skipCaseConversion: true },
            responseType: AiGenerationDirective,
        })
    }

    public async update(
        domainId: number,
        directiveId: number,
        dto: UpdateAiGenerationDirectiveRequest,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<AiGenerationDirective>> {
        return this.request(HttpMethod.PATCH, `${this.getEndpoint(domainId)}/${directiveId}`, {
            ...options,
            data: dto,
            config: { skipCaseConversion: true },
            responseType: AiGenerationDirective,
        })
    }

    /**
     * Compile a free-text directive body into its decomposition without
     * persisting. Returns the tiered {@link CompiledDirective}: the guaranteed `spec`
     * (a depth-1 PQuery document), judge-checked `assertions`, best-effort `style`,
     * and `unmapped` residue. Server-side compile via sealab, Redis-cached by body hash.
     */
    public async compilePreview(
        domainId: number,
        body: string,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<CompiledDirective>> {
        // return { data: sportsIllustratedRes, ok: true }
        return this.request(HttpMethod.POST, `${this.getEndpoint(domainId)}/compile-preview`, {
            ...options,
            data: CompileAiGenerationDirectiveRequest.from({ body }),
            config: { skipCaseConversion: true },
            responseType: CompiledDirective,
        })
    }

    /**
     * Compile the directive's current active version server-side and persist the result
     * onto that version's `compiled_rules` (the confirm write-path) — reuses
     * `compilePreview`'s Redis pre-warm, so this is typically instant right after a save.
     *
     * `expectedCompiledRules` must be the {@link CompiledDirective} the caller already
     * displayed and had approved for this version's body (normally straight from a prior
     * `compilePreview` call). The server independently recompiles and only persists if its
     * own fresh result matches — this call rejects (409) rather than silently persisting a
     * decomposition that drifted from what was actually reviewed.
     */
    public async compileAndPersist(
        domainId: number,
        directiveId: number,
        expectedCompiledRules: CompiledDirective,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<AiGenerationDirective>> {
        return this.request(HttpMethod.POST, `${this.getEndpoint(domainId)}/${directiveId}/compile`, {
            ...options,
            data: CompileAndPersistAiGenerationDirectiveRequest.from({
                expected_compiled_rules: expectedCompiledRules,
            }),
            config: { skipCaseConversion: true },
            responseType: AiGenerationDirective,
        })
    }

    /**
     * Ask which prose-tier statements in a compile are one missing operand away from being
     * enforceable, and for the rewrite that would close the gap.
     *
     * The compile is sent UP rather than referenced by id because the drawer routinely displays
     * a preview that was never persisted. Nothing is written by this call.
     *
     * Purely additive to the editor: a failure here costs the author a hint, not their work, so
     * callers are expected to ignore a bad response rather than surface an error.
     */
    public async suggestPromotions(
        domainId: number,
        compiledRules: CompiledDirective,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<SuggestDirectivePromotionsResponse>> {
        return this.request(HttpMethod.POST, `${this.getEndpoint(domainId)}/suggest-promotions`, {
            ...options,
            data: SuggestDirectivePromotionsRequest.from({ compiled_rules: compiledRules }),
            config: { skipCaseConversion: true },
            responseType: SuggestDirectivePromotionsResponse,
        })
    }

    private getEndpoint(domainId: number): string {
        return `${aqe.defaults.publicApiDomain}/domains/${domainId}/ai-generation-directives`
    }
}
